<template>
  <div class="app-container">
    <AppNavbar />

    <!-- Header with Help toggle -->
    <div class="dashboard-header">
      <h1 v-if="adminName" class="heading">
        Welcome, {{ adminName }}!
      </h1>
      <h1 v-else class="error heading">
        ERROR: Cannot connect to your admin account
      </h1>
      <v-btn icon class="help-btn" @click="showHelp = !showHelp">
        <v-icon>mdi-help-circle-outline</v-icon>
      </v-btn>
    </div>

    <!-- InfoGuide centered under header -->
    <div v-if="showHelp" class="info-container">
      <InfoGuide @close="showHelp = false" />
    </div>

    <!-- Main dashboard content -->
    <div v-else class="dashboard-content">
      <div class="search-bar">
        <input v-model="searchEmail" placeholder="Enter customer email" />
        <button @click="fetchCustomer">Search</button>
      </div>

      <!-- Show error if email doesn't exist -->
      <transition name="fade">
        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
      </transition>

      <transition name="fade">
        <div class="portfolio-wrapper" v-if="customer">
          <CustomerInfoBox
            :customer="customer"
            :isMobile="isMobile"
            @reset-password="resetPassword"
            @resend-activation="resendActivation"
          />
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import AppNavbar from "@/components/AppNavbar.vue";
import CustomerInfoBox from "@/components/CustomerInfoBox.vue";
import InfoGuide from "@/components/InfoGuide.vue";
import api from "@/api";

export default {
  name: "DashboardView",
  components: { AppNavbar, CustomerInfoBox, InfoGuide },
  data() {
    return {
      adminName: "",
      isMobile: window.innerWidth <= 790,
      searchEmail: "",
      customer: null,
      errorMessage: "",
      showHelp: false
    };
  },
  mounted() {
    const token = localStorage.getItem("token");
    if (!token) return this.$router.push("/login");
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    this.loadAdminInfo();
  },
  created() {
    window.addEventListener("resize", this.updateScreenSize);
    this.updateScreenSize();
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.updateScreenSize);
  },
  methods: {
    updateScreenSize() {
      this.isMobile = window.innerWidth <= 790;
    },
    async loadAdminInfo() {
  try {
    const response = await api.get("/api/dashboard");
    const user = response.data.user;

    this.adminName =
      user.fullName ||
      `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
      "Admin";
  } catch (error) {
    console.error("❌ Failed to fetch admin info:", error);
    this.adminName = "Admin";
  }
},
    async fetchCustomer() {
  this.errorMessage = "";
  this.customer = null;

  try {
    const response = await api.get(`/api/user/full/${this.searchEmail}`);
    const user = response.data.user;

    // If no user returned or missing email, treat as not found
    if (!user || !user.email) {
      this.errorMessage = "This email doesn't exist.";
      return;
    }

    this.customer = {
      name:
        user.first_name && user.last_name
          ? `${user.first_name} ${user.last_name}`
          : "Unknown",
      clinic: user.clinicName || user.clinic || "N/A",
      clinicId: user.clinicId || "",
      email: user.email,
      profilePictureUrl: user.profilePictureUrl || "",
      createdAt: user.createdAt || "N/A",
      updatedAt: user.updatedAt || "N/A",
      userId: user.userId || "",
      emailVerified: !!user.emailVerified,
      status: user.status || "Unknown",
      role: user.role || "Unknown",
      gender: user.gender || "N/A",
      address: user.address || "N/A",
      birthDate: user.birthDate || "N/A"
    };
  } catch (error) {
    console.error("❌ API error:", error);
    this.customer = null; // don't display anything
    this.errorMessage = "This email doesn't exist.";
  }
    },
    async resetPassword(email) {
      try {
        await api.post("/api/reset-password", { email });
        alert("Password reset initiated.");
      } catch (error) {
        alert(
          "Failed to reset password: " +
            (error.response?.data?.error || error.message)
        );
      }
    },
    async resendActivation(email) {
      try {
        await api.post("/api/resend-activation", { email });
        alert("Activation email resent.");
      } catch (error) {
        alert(
          "Failed to resend activation email: " +
            (error.response?.data?.error || error.message)
        );
      }
    }
  }
};
</script>

<style scoped>
.app-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #f7fcf8, #f2f4ff, #ffffff);
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Header */
.dashboard-header {
  max-width: 960px;
  margin: 100px auto 0;
  padding: 0 20px;
  display: flex;
  align-items: center;
  position: relative;
}

.heading {
  flex: 1;
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  color: #4a4a4a;
  animation: fadeInSlide 0.6s ease;
}

.error {
  color: #cf1b2b;
}

.help-btn {
  margin-left: 10px;
  transition: background 0.3s ease;
}
.help-btn:hover {
  background-color: #e8f5e9 !important;
}
.help-btn:hover .v-icon {
  color: #5fa83c !important;
}

/* Info Guide */
.info-container {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

/* Main dashboard */
.dashboard-content {
  max-width: 960px;
  margin: 20px auto;
  padding: 0 20px;
  text-align: center;
}

/* Search bar */
.search-bar {
  margin-bottom: 20px;
  display: flex;
  justify-content: center;
  gap: 12px;
  animation: fadeIn 0.6s ease;
}

.search-bar input {
  padding: 12px;
  width: 320px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 16px;
  transition: all 0.3s ease;
  box-shadow: 0 0 0 0 rgba(95, 168, 60, 0.4);
}
.search-bar input:focus {
  outline: none;
  border-color: #5fa83c;
  box-shadow: 0 0 4px 2px rgba(95, 168, 60, 0.2);
}

.search-bar button {
  padding: 12px 24px;
  background-color: #5fa83c;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.2s ease;
}
.search-bar button:hover {
  background-color: #4d8a31;
  transform: scale(1.03);
}

/* Error message under search */
.error-message {
  color: #cf1b2b;
  font-weight: 500;
  margin-top: -10px;
  margin-bottom: 20px;
  font-size: 15px;
  animation: fadeIn 0.3s ease;
}

/* Fade transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease, transform 0.4s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}

/* Customer Info Box */
.portfolio-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 0 10px;
  animation: fadeInSlide 0.8s ease;
}

/* Animations */
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
@keyframes fadeInSlide {
  0% {
    opacity: 0;
    transform: translateY(20px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Mobile */
@media (max-width: 790px) {
  .dashboard-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .dashboard-content {
    margin-top: 80px;
    padding: 15px;
  }

  .search-bar {
    flex-direction: column;
    gap: 12px;
  }

  .search-bar input {
    width: 100%;
  }

  .portfolio-wrapper {
    flex-direction: column;
    align-items: center;
  }
}
</style>
